import { getContextFromFeaturePrd } from "@/components/AI/EngSpec/utils";
import { AI_AGENT } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import {
  Message,
  MessageContext,
  useFeaturePrdForCanvas,
} from "@/store/zustand/supabaseStore";
import { useSession } from "@clerk/clerk-react";
import { v4 as uuidv4 } from "uuid";
import { ChatMessageOptions } from "../types";
import {
  fixJson,
  getAxiosErorrMessage,
  logAxiosError,
  useCanvasId,
} from "../utils";
import { useBaseInputsForPrompt } from "./prompt";
import { generateJsonStream } from "./utils";
import { useCreateMessageForChat } from "@/components/AI/chat/utils/messages";
import { track } from "../events/tracker";

const MAX_PREVIOUS_MESSAGES = 10;

// Add type definition at the top of the file
type MessageStep = {
  text?: string;
  toolCall?: {
    toolCallId: string;
    name: string;
    argumentsParsed: any;
  };
  toolResult?: string;
};

// Add type for partial results
type PartialToolResult = {
  toolCallId: string;
  name: string;
  arguments: string;
  argumentsParsed: any;
};

const constructMessages = (
  usedIds: { [key: number]: string },
  createdAt: { [key: string]: Date },
  steps: Array<{
    text?: string;
    toolCall?: {
      toolCallId: string;
      name: string;
      argumentsParsed: any;
    };
    toolResult?: string;
  }>
) => {
  let idIdx = 0;
  const getId = () => {
    if (!usedIds[idIdx]) {
      usedIds[idIdx] = uuidv4().toString();
      createdAt[usedIds[idIdx]] = new Date();
    }

    idIdx += 1;
    return usedIds[idIdx - 1];
  };

  const newMessages: Message[] = [];
  const shouldPersistToolResultsInHistory = [
    "readFilesTool",
    "applyFileEditsTool",
  ];

  steps.forEach((step) => {
    // Handle text content
    if (step.text) {
      // @ts-expect-error TS2345
      newMessages.push({
        id: getId(),
        role: "assistant",
        content: step.text,
      });
      // @ts-expect-error TS2339
      newMessages[newMessages.length - 1].created_at =
        createdAt[newMessages[newMessages.length - 1].id];
    }

    // Handle tool call and its result
    if (step.toolCall) {
      const contentBlocks = [];
      // @ts-expect-error TS2345
      contentBlocks.push({
        type: "tool-call",
        args: step.toolCall.argumentsParsed,
        toolName: step.toolCall.name,
      });

      // Append tool call message
      // @ts-expect-error TS2345
      newMessages.push({
        id: getId(),
        role: "assistant",
        content: JSON.stringify(contentBlocks),
      });
      // @ts-expect-error TS2339
      newMessages[newMessages.length - 1].created_at =
        createdAt[newMessages[newMessages.length - 1].id];

      // Append tool result message for valid tool names
      if (
        step.toolResult &&
        shouldPersistToolResultsInHistory.includes(step.toolCall.name)
      ) {
        // @ts-expect-error TS2345
        newMessages.push({
          id: getId(),
          role: "tool-result",
          content: step.toolResult,
        });
        // @ts-expect-error TS2339
        newMessages[newMessages.length - 1].created_at =
          createdAt[newMessages[newMessages.length - 1].id];
      }
    }
  });

  return newMessages;
};

const getTextForAIFromMessageContext = (messageContext: MessageContext) => {
  if (!messageContext) {
    return "";
  }

  let fullText = "";

  if (messageContext?.chatOnlyMode) {
    fullText +=
      "This message was in chat only mode, any changes were not applied.\n\n";
  }

  if (messageContext?.filesWithErrors?.length) {
    fullText += "The following files had errors and were not applied:\n";
    fullText += messageContext?.filesWithErrors?.join("\n");
    fullText += "\n\n";
  }

  if (messageContext?.hadError) {
    fullText +=
      "There was an error in this message, none of the changes were applied.\n\n";
  }

  return fullText.trim();
};

const processMessage = (msg: Message, allEditedFiles?: Set<string>) => {
  if (allEditedFiles && msg.message_context?.editedFiles) {
    msg.message_context.editedFiles.forEach((file) => {
      allEditedFiles.add(file);
    });
  }

  // The tool-result role is hard to get to work with vercel ai so just treat it as a user message
  let role = msg.role === "tool-result" ? "user" : msg.role;

  if (msg.role === "tool-result") {
    return {
      role,
      content: msg.content,
    };
  }

  const textForMessageContext = getTextForAIFromMessageContext(
    // @ts-expect-error TS2345
    msg.message_context
  );
  const messageContextBlocks = textForMessageContext
    ? [
        {
          type: "text",
          text: textForMessageContext,
        },
      ]
    : [];

  let inputBlocks;
  if (Array.isArray(msg.content)) {
    inputBlocks = msg.content;
  } else if (typeof msg.content === "string" && msg.content?.startsWith("[")) {
    try {
      inputBlocks = JSON.parse(msg.content);
    } catch (e) {
      console.error(e);
    }
  }

  if (!inputBlocks) {
    return {
      role,
      content: [
        {
          type: "text",
          text: msg.content,
        },
        ...messageContextBlocks,
      ],
    };
  }

  // If it starts with "[" it could be content blocks
  try {
    let invalidBlocks = false;
    const parsedContent = inputBlocks?.map((block) => {
      if (block.type === "text") {
        return {
          type: "text",
          text: block.text,
        };
      } else if (block.type === "tool-call") {
        // Tool call blocks do not currently work with vercel ai SDK as of Nov 19, 2024
        // So just convert them to text blocks
        return {
          type: "text",
          text: `Tool call: ${block.toolName} with args: ${JSON.stringify(
            block.args
          )}`,
        };
      } else {
        invalidBlocks = true;
        return block;
      }
    });

    if (invalidBlocks) {
      return {
        role,
        content: [
          {
            type: "text",
            text: JSON.stringify(parsedContent),
          },
          ...messageContextBlocks,
        ],
      };
    }

    return {
      role,
      // Filter out empty blocks
      content: parsedContent
        .filter((block) => block.type !== "text" || block.text)
        .concat(messageContextBlocks),
    };
  } catch (e) {
    return {
      role,
      content: [
        {
          type: "text",
          text: msg.content,
        },
        ...messageContextBlocks,
      ],
    };
  }
};

export interface AIChatInput {
  feature_title: string;
  feature_prd: string;
  visual_context: any[];
  user_selection_context: string;
  mermaid_diagram: string;
  selected_elements: string;
  storyboards_in_canvas: string;
  use_text_editor: boolean;
  token_soft_limit: number;
  layout_mode: string;
  left_tab: string;
  blind_edited_files: string[];
  chat_only_mode: boolean;
  use_reasoning: boolean;
  use_search: boolean;
  is_free_prompt: boolean;
  reasoning_model: string;
  code_generation_model: string;

  // For aiChatChangeCodeSnippet only
  file_contents?: string;
  diff_code_snippet?: string;
  selected_code_snippet?: string;
}

interface ChatWithAIParams {
  supabaseJWT: string;
  canvasId: string;
  requestPath: string;
  messagesCanSummarize: Message[];
  messagesDoNotSummarize: Message[];
  userPrompt: string;
  aiChatInput: AIChatInput;
  onStream?: (newMessages: Message[]) => void;
  featureFlags?: { supabase_v2: boolean };
  abortController?: AbortController;
  isFixBrokenResponse?: boolean;
}

export const chatWithAI = async ({
  supabaseJWT,
  requestPath,
  canvasId,
  messagesCanSummarize,
  messagesDoNotSummarize,
  userPrompt,
  onStream,
  aiChatInput,
  abortController,
  isFixBrokenResponse,
  featureFlags = { supabase_v2: true },
}: ChatWithAIParams): Promise<{
  newMessages?: Message[];
  usage?: any;
  error?: string;
  filesInContext?: string[];
  traceId?: string;
}> => {
  const allEditedFiles = new Set<string>();

  // Step 1: send the conversation and available functions to GPT
  const chatHistoryMessages: any = [...messagesCanSummarize]
    .slice(-MAX_PREVIOUS_MESSAGES)
    .map((msg) => processMessage(msg, allEditedFiles))
    .filter((msg) => {
      // Filter out empty messages
      if (Array.isArray(msg.content)) {
        return msg.content.length > 0;
      }

      return msg.content;
    });

  const chatHistoryDoNotSummarizeMessages: any = [...messagesDoNotSummarize]
    .map((msg) => processMessage(msg))
    .filter((msg) => {
      // Filter out empty messages
      if (Array.isArray(msg.content)) {
        return msg.content.length > 0;
      }
      return msg.content;
    });

  const url = `${
    import.meta.env.VITE_BACKEND_BASE_URL
  }/canvases/${canvasId}/ai/vercel-ai/${requestPath}`;

  const posthog_flags = [];

  if (featureFlags?.supabase_v2) {
    // @ts-expect-error TS2345
    posthog_flags.push("supabase-v2");
  }

  const body = {
    chat_history: chatHistoryMessages,
    chat_history_to_not_summarize: chatHistoryDoNotSummarizeMessages,
    user_prompt: userPrompt,
    posthog_flags,
    all_edited_files: Array.from(allEditedFiles),
    is_fix_broken_response: isFixBrokenResponse,
    ...aiChatInput,
  };

  const stream = await generateJsonStream(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${supabaseJWT}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal: abortController?.signal,
  });

  let res = null;
  let streamingSteps: MessageStep[] = [];
  let currentText = "";
  let currentToolCall: PartialToolResult | null = null;
  let usedIds: { [key: number]: string } = {};
  let createdAt: { [key: string]: Date } = {};

  for await (let chunk of stream) {
    if (chunk.status === -1 && chunk.message === "intermediate") {
      const parsedChunk = JSON.parse(chunk.data);

      // Tool final value
      if (parsedChunk.type === "tool-call") {
        // If we have accumulated text, add it as a step
        if (currentText) {
          streamingSteps.push({ text: currentText });
          currentText = "";
        }

        // Create new tool call
        currentToolCall = {
          toolCallId: parsedChunk.fullValue.toolCallId,
          name: parsedChunk.fullValue.toolName,
          arguments: "",
          argumentsParsed: parsedChunk.fullValue.args,
        };
      }

      // Tool streaming
      if (parsedChunk.type === "tool-call-delta") {
        if (!currentToolCall) {
          currentToolCall = {
            toolCallId: parsedChunk.chunk.toolCallId,
            name: parsedChunk.chunk.toolName,
            arguments: "",
            argumentsParsed: null,
          };
        }
        currentToolCall.arguments += parsedChunk.chunk.argsTextDelta;
        currentToolCall.argumentsParsed = fixJson(
          currentToolCall.arguments
        ).fixedObj;
      }

      // Text streaming
      if (parsedChunk.type === "text-delta") {
        // If we have a pending tool call, add it as a step
        if (currentToolCall) {
          streamingSteps.push({
            toolCall: {
              toolCallId: currentToolCall.toolCallId,
              name: currentToolCall.name,
              argumentsParsed: currentToolCall.argumentsParsed,
            },
          });
          currentToolCall = null;
        }
        currentText += parsedChunk.chunk;
      }

      // Create messages from current state
      const currentSteps = [...streamingSteps];
      if (currentText) {
        currentSteps.push({ text: currentText });
      }
      if (currentToolCall) {
        currentSteps.push({
          toolCall: {
            toolCallId: currentToolCall.toolCallId,
            name: currentToolCall.name,
            argumentsParsed: currentToolCall.argumentsParsed,
          },
        });
      }

      const newMessages = constructMessages(usedIds, createdAt, currentSteps);
      if (newMessages?.length) {
        onStream?.(newMessages);
      }
    } else {
      res = chunk;
      break;
    }
  }

  // @ts-expect-error TS18047
  if (res.status !== 200) {
    console.error({
      // @ts-expect-error TS18047
      msg: res.message,
      // @ts-expect-error TS18047
      internalErrorStr: res.internalErrorStr,
    });
    // @ts-expect-error TS18047
    return { error: res.message };
  }
  // TODO: Get rid of searchSummaryDeprecated on or after 14th March 2025. It exists for backwards compatibility.
  const {
    steps,
    reasoning,
    affectedFiles,
    planOfAction,
    search,
    searchSummaryDeprecated,
    traceId,
    // @ts-expect-error TS18047
  } = res.data;

  // @ts-expect-error TS18047
  const filesInContext = res.data.filesInContext;
  const finalText = steps[steps.length - 1].text;

  const reasoningObject = {
    reasoning: reasoning || "",
    plan_of_action: planOfAction || "",
    affected_files: affectedFiles || [],
    summary_of_search_results: search?.summary || searchSummaryDeprecated || "",
  };

  let reasoningText = "";
  if (reasoning) {
    reasoningText = `<reasoning>\n${JSON.stringify(
      reasoningObject
    )}\n</reasoning>\n`;
  }

  let searchGroundingSourcesText = "";
  if (search?.sources?.length) {
    searchGroundingSourcesText = `\n<search-grounding-sources>\n${JSON.stringify(
      {
        searchGroundingSources: search.sources,
      }
    )}\n</search-grounding-sources>\n`;
  }

  const finalSteps: MessageStep[] = [
    ...(reasoningText ? [{ text: reasoningText }] : []),
    ...streamingSteps,
    ...(finalText ? [{ text: finalText }] : []),
    ...(searchGroundingSourcesText
      ? [{ text: searchGroundingSourcesText }]
      : []),
  ];

  const finalMessages = constructMessages(usedIds, createdAt, finalSteps);
  return {
    newMessages: finalMessages,
    filesInContext: filesInContext,
    traceId,
  };
};

export const useChatWithAI = () => {
  const canvasId = useCanvasId();
  const { session } = useSession();
  const baseChatInput = useBaseInputsForPrompt();
  const codeSnippet = useAiContextStore(
    (state) => state.chatContextCodeSnippet
  );

  // Context the user added in the PRD tab
  const featurePrd = useFeaturePrdForCanvas();
  const { contextWithImages: prdImageContext } =
    getContextFromFeaturePrd(featurePrd);

  const { createAssistantMessage } = useCreateMessageForChat();

  return async ({
    isRetry,
    abortController,
    onStreamedMessages,
    ...messageOpts
  }: ChatMessageOptions & {
    isRetry?: boolean;
    abortController?: AbortController;
    onStreamedMessages: (messages: Message[]) => void;
  }) => {
    const supabaseJWT = (await session?.getToken()) || "";
    const visualContext = [
      ...prdImageContext,
      ...(messageOpts.attachedFiles || []),
    ];

    const requestPath = codeSnippet
      ? "aiChatChangeCodeSnippet"
      : messageOpts.aiAgent === AI_AGENT.MAX_AGENT
      ? "aiChatMaxAgent"
      : "aiChat";

    const aiChatInput = {
      ...baseChatInput,
      use_text_editor: !messageOpts.disableTextEditor,
      blind_edited_files: messageOpts.criticalFiles ?? [], // Its called blind_edited_files but in the backend its treated as "critical files to always include"
      chat_only_mode: messageOpts.chatOnlyMode,
      use_reasoning: messageOpts.useReasoning,
      use_search: messageOpts.useSearch,
      is_free_prompt: messageOpts.isFreePrompt,
      reasoning_model: messageOpts.reasoningModel,
      code_generation_model: messageOpts.codeGenerationModel,
      visual_context: visualContext,
    };
    try {
      const { newMessages, error, filesInContext, traceId } = await chatWithAI({
        requestPath,
        canvasId,
        messagesCanSummarize: messageOpts.messagesCanSummarize,
        messagesDoNotSummarize: messageOpts.messagesDoNotSummarize,
        supabaseJWT,
        userPrompt: messageOpts.userPrompt,
        onStream: onStreamedMessages,
        abortController,
        aiChatInput,
        isFixBrokenResponse: isRetry,
      });

      return {
        newMessages,
        error,
        filesInContext,
        requestPath,
        traceId,
      };
    } catch (e) {
      logAxiosError("Error sending message to AI", e);
      const status = (e as any)?.response?.status;
      const isServerError = typeof status === "number" && status >= 500;

      const messages: Message[] = [];
      if (e.name.includes("AbortError")) {
        track("AI_CHAT_WARNING", {
          warning: `Chat response aborted`,
        });
      } else {
        const data = {
          requestPath,
          isRetry,
          message: getAxiosErorrMessage(e),
          ...(typeof status === "number" && { status_code: status }),
        };

        if (isServerError) {
          track("AI_CHAT_ERROR", {
            error: `Server Error calling chatWithAI endpoint (Status ${status})`,
            data,
          });
        } else {
          track("AI_CHAT_WARNING", {
            warning: `Non-500 error calling chatWithAI endpoint`,
            data,
          });
        }
        messages.push(
          createAssistantMessage({
            content: `There's been an error processing your request. Please try again.`,
            message_context: {
              hadError: true,
            },
          })
        );
      }

      return {
        newMessages: messages,
        error: `Error thrown while calling chatWithAI: ${getAxiosErorrMessage(
          e
        )}`,
        filesInContext: [],
        requestPath,
        traceId: undefined,
      };
    }
  };
};
